import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { createSupabaseAuthLock, SupabaseService } from '@packages/shared-data/services';
import { isBrowserNetworkError } from '@packages/shared-utils';
import { AuthError, RealtimeChannel, SupabaseClient, Session } from '@supabase/supabase-js';
import { Observable, firstValueFrom, from, BehaviorSubject, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';

import { environment } from '@environment';

const BROKER_ACCESS_DEACTIVATED_FLAG = 'leasity.brokerAccessDeactivated';
const SIGNED_OUT_SIGNAL_WAIT_MS = 1500;
const ACCESS_SIGNAL_RESUBSCRIBE_BASE_DELAY_MS = 1000;
const ACCESS_SIGNAL_RESUBSCRIBE_MAX_DELAY_MS = 30000;
const ACCESS_SIGNAL_MAX_RESUBSCRIBE_ATTEMPTS = 5;
const ACCESS_SIGNAL_SOCKET_SETTLE_TIMEOUT_MS = 2000;
const ACCESS_SIGNAL_SOCKET_SETTLE_POLL_MS = 25;

interface AuthUser {
  id: string;
  email: string;
}

export type BrokerPortalAccountStatus = 'missing_profile' | 'deactivated' | 'operable';

export interface AuthState {
  user: AuthUser;
  jwtPayload: JWTPayload;
  missingBrokerProfile: boolean;
  deactivatedBrokerAccess: boolean;
  brokerAccountStatus: BrokerPortalAccountStatus | null;
}

export interface JWTPayload {
  agent_id?: string | null;
  agent_roles?: Record<string, 'admin' | 'member'> | null;
  company_user_id?: string | null;
  company_roles?: Record<string, 'admin' | 'editor' | 'viewer'> | null;
  property_manager_id?: string | null;
  property_manager_roles?: Record<string, 'admin' | 'editor' | 'viewer'> | null;
}

export function hasLinkedBrokerProfile(claims: JWTPayload): boolean {
  return Boolean(claims.agent_id);
}

export function isMissingBrokerProfile(claims: JWTPayload): boolean {
  return !hasLinkedBrokerProfile(claims);
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private supabaseClient!: SupabaseClient;
  private authStateSubject = new BehaviorSubject<AuthState | null>(null);
  public authState$ = this.authStateSubject.asObservable();
  private _companyScopedDenialSubject = new Subject<void>();
  /** Emitted when a Supabase 403 suggests company-scoped broker access was denied mid-session. */
  public companyScopedDenial$ = this._companyScopedDenialSubject.asObservable();
  private _isInitialized = false;
  /**
   * Resolves once `initializeAuthListener()` has published its first auth state.
   * `AuthGuard` awaits this so a route is never activated while `authState$` is
   * still the BehaviorSubject's initial `null`.
   */
  private _initialization: Promise<void> = Promise.resolve();
  private _didAttemptClaimsRefresh = false;
  private _userInitiatedSignOut = false;
  private _accessSignalsChannel: RealtimeChannel | null = null;
  /**
   * Removal of the previous channel, including waiting out the socket close it
   * triggers. A re-join must await this or it lands on a closing transport.
   */
  private _accessSignalChannelRemoval: Promise<void> | null = null;
  private _signedOutHandshakeEventId: number | null = null;
  private _signedOutEventId = 0;
  private _subscribedAuthUserId: string | null = null;
  private _authEventGeneration = 0;
  private _accessSignalChannelGeneration = 0;
  private _accessSignalResubscribeAttempts = 0;
  private _accessSignalResubscribeTimer: ReturnType<typeof setTimeout> | null = null;
  private _accessSignalChannelLive = false;
  private _channelRecoveryListener: (() => void) | null = null;

  private router = inject(Router);

  constructor() {
    // Bridge so the custom fetch wrapper can notify after `this` is constructed.
    const denialBridge = { notify: (): void => undefined };

    const baseFetch = globalThis.fetch.bind(globalThis);
    SupabaseService.initialize(environment.supabaseUrl, environment.supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storageKey: 'sb-brokers-portal-auth-token',
        lock: createSupabaseAuthLock(),
      },
      global: {
        fetch: async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
          const response = await baseFetch(input, init);
          try {
            const url =
              typeof input === 'string'
                ? input
                : input instanceof URL
                  ? input.href
                  : input.url;
            if (
              response.status === 403 &&
              url.startsWith(environment.supabaseUrl) &&
              denialBridge.notify !== undefined
            ) {
              denialBridge.notify();
            }
          } catch {
            // Never break Supabase traffic on denial-side effects
          }
          return response;
        },
      },
    });
    this.supabaseClient = SupabaseService.getClient();
    denialBridge.notify = () => {
      if (this.isAuthenticatedSync) {
        this.notifyCompanyScopedDenial();
      }
    };
    this._initialization = this.initializeAuthListener();
    this._listenForChannelRecovery();
  }

  /**
   * A backgrounded or unfocused tab has its timers throttled and its Realtime
   * heartbeat starved, so the channel can die and the bounded backoff can burn
   * through every attempt while nobody is looking. Coming back to the tab is the
   * moment the block matters again — retry from scratch whenever the channel is
   * not live. Reconciliation on SUBSCRIBED then picks up any signal missed while
   * it was down.
   *
   * Both events are needed: switching away from and back to the tab fires
   * `visibilitychange`, but a window that loses and regains OS focus while this
   * tab stays selected stays `visible` and only fires `focus`.
   */
  private _listenForChannelRecovery(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    this._channelRecoveryListener = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      this._recoverAccessSignalChannelIfDead();
    };
    document.addEventListener('visibilitychange', this._channelRecoveryListener);
    window.addEventListener('focus', this._channelRecoveryListener);
  }

  private _recoverAccessSignalChannelIfDead(): void {
    const authUserId = this._subscribedAuthUserId;
    if (!authUserId || this._accessSignalChannelLive) {
      return;
    }
    this._accessSignalResubscribeAttempts = 0;
    this._clearAccessSignalResubscribeTimer();
    const generation = ++this._accessSignalChannelGeneration;
    this._removeAccessSignalChannel();
    void this._joinAccessSignalChannel(authUserId, generation);
  }

  /**
   * Initialize auth state listener
   */
  private async initializeAuthListener(): Promise<void> {
    try {
      const {
        data: { session },
        error,
      } = await this.supabaseClient.auth.getSession();
      if (error) {
        console.warn('Auth session error (non-critical):', error.message);
      }
      const mappedSession = await this._ensurePortalClaimsThenMapSession(session);
      this.authStateSubject.next(mappedSession);
      this._syncAccessSignalSubscription(mappedSession?.user.id ?? null);

      if (mappedSession && this.router.url.startsWith('/auth/')) {
        void this.router.navigate(['/authenticated']);
      } else if (
        !mappedSession &&
        this.getBrokerAccessDeactivatedFlag() &&
        this.router.url.startsWith('/auth/')
      ) {
        void this.router.navigate(['/authenticated']);
      }
    } catch (err: unknown) {
      console.warn('Failed to restore session:', err instanceof Error ? err.message : String(err));
      this.authStateSubject.next(null);
    } finally {
      this._isInitialized = true;
    }

    this.supabaseClient.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // Invalidate in-flight session mapping so a late TOKEN_REFRESHED/SIGNED_IN
        // promise cannot republish a stale authenticated state after teardown.
        this._authEventGeneration += 1;
        const signedOutEventId = ++this._signedOutEventId;
        void this._handleSignedOut(signedOutEventId);
        return;
      }

      const generation = ++this._authEventGeneration;
      void this._ensurePortalClaimsThenMapSession(session).then((mappedSession) => {
        if (generation !== this._authEventGeneration) {
          return;
        }
        this.authStateSubject.next(mappedSession);
        this._syncAccessSignalSubscription(mappedSession?.user.id ?? null);

        if (mappedSession && this.router.url.startsWith('/auth/')) {
          void this.router.navigate(['/authenticated']);
        }
      });
    });
  }

  /**
   * Whether there is an authenticated session **with its portal claims already
   * mapped**.
   *
   * This used to answer from `getSession()` while initialization was still in
   * flight, which let `AuthGuard` admit `/authenticated` before `authState$`
   * had emitted. Every component reading claims in `ngOnInit` then raced the
   * mapping and got `User claims not initialized or user is not an agent` —
   * seen in `HomeComponent` (#625), `ApplicationsComponent` (#628) and
   * `NewApplicationComponent`, where it degraded silently into a form missing
   * its guarantor toggle.
   *
   * Awaiting initialization removes the race for every consumer at once, rather
   * than each component deferring itself. It waits for the state to be
   * *resolved*, not for it to be non-null, so a session without portal claims
   * still answers `false` promptly instead of hanging.
   */
  async isAuthenticated(): Promise<boolean> {
    if (!this._isInitialized) {
      await this._initialization;
    }
    return !!this.authStateSubject.value;
  }

  get isAuthenticatedSync(): boolean {
    return !!this.authStateSubject.value;
  }

  get currentUser(): Promise<AuthUser | null> {
    return firstValueFrom(this.authState$.pipe(map((state) => state?.user ?? null)));
  }

  getBrokerAccessDeactivatedFlag(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    return sessionStorage.getItem(BROKER_ACCESS_DEACTIVATED_FLAG) === '1';
  }

  markBrokerAccessDeactivated(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(BROKER_ACCESS_DEACTIVATED_FLAG, '1');
  }

  clearBrokerAccessDeactivatedFlag(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.removeItem(BROKER_ACCESS_DEACTIVATED_FLAG);
  }

  /**
   * Notify layout to recover company context after a company-scoped RLS denial
   * (partial membership deactivation while JWT/company context is still selected).
   */
  notifyCompanyScopedDenial(): void {
    this._companyScopedDenialSubject.next();
  }

  /**
   * When active memberships are empty mid-session (e.g. last membership
   * deactivated for a multi-role user who kept a session), revalidate via
   * account-status RPC and enter deactivated UX when confirmed.
   */
  async confirmDeactivatedAccessIfNeeded(): Promise<boolean> {
    const status = await this._fetchBrokerPortalAccountStatus();
    if (status === 'operable') {
      this.clearBrokerAccessDeactivatedFlag();
      void this._consumeOwnAccessSignals();
      return false;
    }
    if (status === 'deactivated' || (status == null && this.getBrokerAccessDeactivatedFlag())) {
      this._applyDeactivatedAccessState();
      return true;
    }
    return false;
  }

  sendOtp(email: string): Observable<{ error: AuthError | null }> {
    return from(
      this.supabaseClient.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: {
          shouldCreateUser: true,
        },
      }),
    );
  }

  verifyOtp(email: string, token: string): Observable<{ error: AuthError | null }> {
    return from(
      this.supabaseClient.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: token.trim(),
        type: 'email',
      }),
    ).pipe(
      tap(({ error }) => {
        if (!error) {
          this.router.navigate(['/authenticated']);
        }
      }),
    );
  }

  /**
   * Explicit leave-session from deactivated / missing-profile pages.
   * Clears the verified deactivation flag and marks user-initiated sign-out
   * before calling Supabase so SIGNED_OUT reaches generic login (no loop).
   */
  signOutFromDeactivatedAccess(): Observable<{ error: AuthError | null }> {
    this._userInitiatedSignOut = true;
    this.clearBrokerAccessDeactivatedFlag();
    void this._consumeOwnAccessSignals();
    return from(this.supabaseClient.auth.signOut());
  }

  signOut(): Observable<{ error: AuthError | null }> {
    this._userInitiatedSignOut = true;
    this.clearBrokerAccessDeactivatedFlag();
    return from(this.supabaseClient.auth.signOut());
  }

  ngOnDestroy(): void {
    this._signedOutHandshakeEventId = null;
    if (
      this._channelRecoveryListener &&
      typeof document !== 'undefined' &&
      typeof window !== 'undefined'
    ) {
      document.removeEventListener('visibilitychange', this._channelRecoveryListener);
      window.removeEventListener('focus', this._channelRecoveryListener);
      this._channelRecoveryListener = null;
    }
    this._teardownAccessSignalSubscription();
    try {
      SupabaseService.destroy();
    } catch (error) {
      console.warn('Error during auth service cleanup:', error);
    }
  }

  private async _handleSignedOut(signedOutEventId: number): Promise<void> {
    const supersededByAuth = (): boolean =>
      this.authStateSubject.value !== null && signedOutEventId !== this._signedOutEventId;
    const staleSignedOut = (): boolean => signedOutEventId !== this._signedOutEventId;

    this._didAttemptClaimsRefresh = false;
    const generationAtStart = this._authEventGeneration;

    try {
      if (this._userInitiatedSignOut) {
        this._userInitiatedSignOut = false;
        if (staleSignedOut() || supersededByAuth()) {
          return;
        }
        this.authStateSubject.next(null);
        this.clearBrokerAccessDeactivatedFlag();
        this._teardownAccessSignalSubscription();
        await this.router.navigate(['/auth/login']);
        return;
      }

      if (this.getBrokerAccessDeactivatedFlag()) {
        if (staleSignedOut() || supersededByAuth()) {
          return;
        }
        this.authStateSubject.next(null);
        this._teardownAccessSignalSubscription();
        await this.router.navigate(['/authenticated']);
        return;
      }

      this._signedOutHandshakeEventId = signedOutEventId;
      const foundSignal = await this._waitForDeactivationSignal(
        SIGNED_OUT_SIGNAL_WAIT_MS,
        generationAtStart,
        signedOutEventId,
      );
      this._signedOutHandshakeEventId = null;
      if (staleSignedOut() || supersededByAuth()) {
        return;
      }
      if (foundSignal || this.getBrokerAccessDeactivatedFlag()) {
        this.markBrokerAccessDeactivated();
        this.authStateSubject.next(null);
        this._teardownAccessSignalSubscription();
        await this.router.navigate(['/authenticated']);
        return;
      }

      this.authStateSubject.next(null);
      this._teardownAccessSignalSubscription();
      await this.router.navigate(['/auth/login']);
    } finally {
      // no global in-flight gate — newest SIGNED_OUT wins via signedOutEventId
    }
  }

  private _waitForDeactivationSignal(
    timeoutMs: number,
    generationAtStart: number,
    signedOutEventId: number,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      let settled = false;
      let localTimer: ReturnType<typeof setTimeout> | null = null;

      const finish = (found: boolean): void => {
        if (settled) {
          return;
        }
        settled = true;
        if (localTimer) {
          clearTimeout(localTimer);
        }
        resolve(found);
      };

      const stale = (): boolean =>
        generationAtStart !== this._authEventGeneration ||
        signedOutEventId !== this._signedOutEventId;

      void this._selectUnconsumedDeactivationSignals().then((found) => {
        if (stale()) {
          finish(false);
          return;
        }
        if (found) {
          this.markBrokerAccessDeactivated();
          finish(true);
        }
      });

      localTimer = setTimeout(() => {
        if (stale()) {
          finish(false);
          return;
        }
        finish(this.getBrokerAccessDeactivatedFlag());
      }, timeoutMs);
    });
  }

  private async _mapSessionToAuthUserSession(session: Session | null): Promise<AuthState | null> {
    if (!session || !session.access_token) {
      return null;
    }
    const claims = jwtDecode<JWTPayload>(session.access_token);
    const brokerAccountStatus = await this._fetchBrokerPortalAccountStatus();

    if (brokerAccountStatus === 'operable') {
      this.clearBrokerAccessDeactivatedFlag();
      void this._consumeOwnAccessSignals();
    } else if (brokerAccountStatus === 'deactivated') {
      this.markBrokerAccessDeactivated();
    }

    const deactivatedBrokerAccess =
      brokerAccountStatus === 'deactivated' ||
      (brokerAccountStatus == null && this.getBrokerAccessDeactivatedFlag());

    const missingBrokerProfile =
      brokerAccountStatus === 'missing_profile' ||
      (brokerAccountStatus == null &&
        isMissingBrokerProfile(claims) &&
        !deactivatedBrokerAccess);

    return {
      user: { id: session.user.id, email: session.user.email as string },
      jwtPayload: claims,
      missingBrokerProfile,
      deactivatedBrokerAccess,
      brokerAccountStatus,
    };
  }

  private async _ensurePortalClaimsThenMapSession(
    session: Session | null,
  ): Promise<AuthState | null> {
    if (!session?.access_token) {
      return null;
    }

    const claims = jwtDecode<JWTPayload>(session.access_token);
    const hasPortalClaims = Boolean(
      claims.agent_id || claims.company_user_id || claims.property_manager_id,
    );
    if (hasPortalClaims || this._didAttemptClaimsRefresh) {
      return this._mapSessionToAuthUserSession(session);
    }

    this._didAttemptClaimsRefresh = true;
    try {
      const { data, error } = await this.supabaseClient.auth.refreshSession();
      if (error) {
        console.warn(
          '[AuthService] refreshSession after sign-in failed (non-critical):',
          error.message,
        );
        return this._mapSessionToAuthUserSession(session);
      }
      return this._mapSessionToAuthUserSession(data.session ?? session);
    } catch (networkError: unknown) {
      const log = isBrowserNetworkError(networkError) ? console.warn : console.error;
      log(
        '[AuthService] refreshSession threw during non-critical claims refresh:',
        networkError instanceof Error ? networkError.message : String(networkError),
      );
      return this._mapSessionToAuthUserSession(session);
    }
  }

  private async _fetchBrokerPortalAccountStatus(): Promise<BrokerPortalAccountStatus | null> {
    try {
      const { data, error } = await this.supabaseClient.rpc('get_broker_portal_account_status');
      if (error) {
        console.warn('[AuthService] get_broker_portal_account_status failed:', error.message);
        return null;
      }
      if (data === 'missing_profile' || data === 'deactivated' || data === 'operable') {
        return data;
      }
      return null;
    } catch (err: unknown) {
      console.warn(
        '[AuthService] get_broker_portal_account_status threw:',
        err instanceof Error ? err.message : String(err),
      );
      return null;
    }
  }

  private _syncAccessSignalSubscription(authUserId: string | null): void {
    if (!authUserId) {
      this._teardownAccessSignalSubscription();
      return;
    }
    if (this._subscribedAuthUserId === authUserId && this._accessSignalsChannel) {
      return;
    }

    // Teardown bumps the generation, so the value below is unique to this channel.
    this._teardownAccessSignalSubscription();
    this._subscribedAuthUserId = authUserId;
    void this._joinAccessSignalChannel(authUserId, this._accessSignalChannelGeneration);
  }

  /**
   * Push the user JWT onto the Realtime socket, then join the access-signal channel.
   *
   * `RealtimeChannel.subscribe()` reads `socket.accessTokenValue`
   * *synchronously* to build the join payload, and `Push.updatePayload()`
   * replaces the payload object rather than mutating it — so a `setAuth()` that
   * lands after the join push was created can never retrofit the token onto it.
   * A channel that joins without the JWT is authorized as `anon`, the owner
   * SELECT policy (`auth_user_id = auth.uid()`) matches nothing, and no INSERT
   * is ever delivered even though the channel reports `SUBSCRIBED`.
   *
   * Today `supabase-js` happens to win that race: `GoTrueClient._recoverAndRefresh()`
   * emits `SIGNED_IN` for a restored session, which `_handleTokenChanged` turns
   * into `realtime.setAuth(token)` before our `getSession()` resolves. That is
   * incidental — it depends on which event the auth client emits on restore,
   * and it is not something this portal should rely on. Awaiting `setAuth`
   * ourselves makes the JWT on the join payload unconditional (LEA-277).
   */
  private async _joinAccessSignalChannel(
    authUserId: string,
    generation: number,
  ): Promise<void> {
    const pendingRemoval = this._accessSignalChannelRemoval;
    if (pendingRemoval) {
      await pendingRemoval;
      if (this._accessSignalChannelRemoval === pendingRemoval) {
        this._accessSignalChannelRemoval = null;
      }
      if (this._isStaleAccessSignalGeneration(generation)) {
        return;
      }
    }

    const authorized = await this._applyRealtimeAuth();
    if (this._isStaleAccessSignalGeneration(generation)) {
      return;
    }
    if (!authorized) {
      // Without a JWT on the socket the channel can only ever join as anon and
      // would silently receive nothing. Retry instead of joining blind.
      this._scheduleAccessSignalResubscribe(authUserId, generation);
      return;
    }

    this._accessSignalsChannel = this.supabaseClient
      .channel(`broker-portal-access-signals:${authUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'broker_portal_access_signals',
          filter: `auth_user_id=eq.${authUserId}`,
        },
        () => {
          void this._onBrokerDeactivatedSignalInsert();
        },
      )
      .subscribe((status) => {
        if (this._isStaleAccessSignalGeneration(generation)) {
          return;
        }
        if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          this._accessSignalChannelLive = false;
          // A dead channel is indistinguishable from "no deactivation happened",
          // so never leave it dead silently.
          this._scheduleAccessSignalResubscribe(authUserId, generation);
          return;
        }
        if (status !== 'SUBSCRIBED') {
          return;
        }
        this._accessSignalChannelLive = true;
        this._accessSignalResubscribeAttempts = 0;
        // Reconcile only after the channel is live so INSERTs between init
        // and SUBSCRIBED are not missed.
        void this._selectUnconsumedDeactivationSignals().then((found) => {
          if (found) {
            void this._onBrokerDeactivatedSignalInsert();
          }
        });
      });
  }

  /**
   * Forward the current access token to the Realtime socket.
   * Returns false when no session token is available (nothing to authorize with).
   */
  private async _applyRealtimeAuth(): Promise<boolean> {
    try {
      const {
        data: { session },
      } = await this.supabaseClient.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        return false;
      }
      await this.supabaseClient.realtime.setAuth(accessToken);
      return true;
    } catch (err: unknown) {
      console.warn(
        '[AuthService] realtime setAuth failed:',
        err instanceof Error ? err.message : String(err),
      );
      return false;
    }
  }

  private _isStaleAccessSignalGeneration(generation: number): boolean {
    return generation !== this._accessSignalChannelGeneration;
  }

  private _scheduleAccessSignalResubscribe(authUserId: string, generation: number): void {
    if (this._isStaleAccessSignalGeneration(generation)) {
      return;
    }
    if (this._accessSignalResubscribeAttempts >= ACCESS_SIGNAL_MAX_RESUBSCRIBE_ATTEMPTS) {
      console.warn(
        '[AuthService] access-signal channel gave up after',
        this._accessSignalResubscribeAttempts,
        'attempts; live deactivation block is down until this tab is focused again',
      );
      return;
    }
    const attempt = this._accessSignalResubscribeAttempts++;
    const delayMs = Math.min(
      ACCESS_SIGNAL_RESUBSCRIBE_BASE_DELAY_MS * 2 ** attempt,
      ACCESS_SIGNAL_RESUBSCRIBE_MAX_DELAY_MS,
    );
    this._clearAccessSignalResubscribeTimer();
    this._accessSignalResubscribeTimer = setTimeout(() => {
      this._accessSignalResubscribeTimer = null;
      if (this._isStaleAccessSignalGeneration(generation)) {
        return;
      }
      // Bump first so the outgoing channel's own CLOSED callback is treated as
      // stale and cannot schedule a second, competing retry.
      const nextGeneration = ++this._accessSignalChannelGeneration;
      this._removeAccessSignalChannel();
      void this._joinAccessSignalChannel(authUserId, nextGeneration);
    }, delayMs);
  }

  private _clearAccessSignalResubscribeTimer(): void {
    if (this._accessSignalResubscribeTimer !== null) {
      clearTimeout(this._accessSignalResubscribeTimer);
      this._accessSignalResubscribeTimer = null;
    }
  }

  private _removeAccessSignalChannel(): void {
    const channel = this._accessSignalsChannel;
    if (!channel) {
      return;
    }
    this._accessSignalsChannel = null;
    // This is the portal's only Realtime channel, so removing it drops
    // supabase-js to zero channels and makes it disconnect the shared socket.
    // Track that teardown so the next join can wait for it — see
    // `_waitForSocketCloseToSettle`.
    this._accessSignalChannelRemoval = this.supabaseClient
      .removeChannel(channel)
      .then(() => this._waitForSocketCloseToSettle())
      .catch(() => undefined);
  }

  /**
   * Wait out an in-flight socket close.
   *
   * `RealtimeClient.connect()` returns early while the socket is still closing,
   * and `subscribe()` calls it internally — so a channel joined during that
   * window is pushed onto a dead transport and sits there until the join times
   * out (10s), before the resubscribe backoff has even started. Recovery on tab
   * focus would then take >10s every time, which is exactly the case where the
   * user is looking at a stale shell.
   */
  private async _waitForSocketCloseToSettle(): Promise<void> {
    const realtime = this.supabaseClient.realtime;
    if (typeof realtime?.isDisconnecting !== 'function') {
      return;
    }
    const deadline = Date.now() + ACCESS_SIGNAL_SOCKET_SETTLE_TIMEOUT_MS;
    while (realtime.isDisconnecting() && Date.now() < deadline) {
      await new Promise((resolve) =>
        setTimeout(resolve, ACCESS_SIGNAL_SOCKET_SETTLE_POLL_MS),
      );
    }
  }

  private async _onBrokerDeactivatedSignalInsert(): Promise<void> {
    // During SIGNED_OUT handshake the session is already gone, so status RPC
    // cannot confirm deactivated — feed the Realtime event into the waiter flag.
    if (this._signedOutHandshakeEventId !== null) {
      this.markBrokerAccessDeactivated();
      return;
    }

    // Stale Realtime events after reactivation must not block an operable account.
    const status = await this._fetchBrokerPortalAccountStatus();
    if (status === 'operable') {
      this.clearBrokerAccessDeactivatedFlag();
      void this._consumeOwnAccessSignals();
      return;
    }
    // Only block when the RPC confirms deactivated. A transient null (network)
    // must not set the persistent flag / auth state after a reactivation race;
    // unconsumed signals remain available for the SIGNED_OUT handshake SELECT.
    if (status === 'deactivated') {
      this._applyDeactivatedAccessState();
    }
  }

  private _applyDeactivatedAccessState(): void {
    this.markBrokerAccessDeactivated();
    const current = this.authStateSubject.value;
    if (current && !current.deactivatedBrokerAccess) {
      this.authStateSubject.next({
        ...current,
        deactivatedBrokerAccess: true,
        missingBrokerProfile: false,
        brokerAccountStatus: 'deactivated',
      });
    }
  }

  private _teardownAccessSignalSubscription(): void {
    this._accessSignalChannelGeneration += 1;
    this._accessSignalResubscribeAttempts = 0;
    this._accessSignalChannelLive = false;
    this._clearAccessSignalResubscribeTimer();
    this._removeAccessSignalChannel();
    this._subscribedAuthUserId = null;
  }

  private async _selectUnconsumedDeactivationSignals(): Promise<boolean> {
    try {
      const { data, error } = await this.supabaseClient
        .from('broker_portal_access_signals')
        .select('id, signal_type')
        .eq('signal_type', 'broker_deactivated')
        .is('consumed_at', null)
        .limit(1);

      if (error) {
        console.warn('[AuthService] signal SELECT failed:', error.message);
        return false;
      }

      if (data && data.length > 0) {
        // Do not set the persistent flag here — callers confirm via account-status
        // (Realtime/reconciliation) or explicitly mark during the SIGNED_OUT handshake.
        return true;
      }
      return false;
    } catch (err: unknown) {
      console.warn(
        '[AuthService] signal SELECT threw:',
        err instanceof Error ? err.message : String(err),
      );
      return false;
    }
  }

  private async _consumeOwnAccessSignals(): Promise<void> {
    try {
      await this.supabaseClient
        .from('broker_portal_access_signals')
        .update({ consumed_at: new Date().toISOString() })
        .eq('signal_type', 'broker_deactivated')
        .is('consumed_at', null);
    } catch (err: unknown) {
      console.warn(
        '[AuthService] consume signals failed:',
        err instanceof Error ? err.message : String(err),
      );
    }
  }
}
